import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import {
    MeasuringPointLeqAlarmExceedancesDocument,
    type MeasuringPointLeqAlarmExceedancesQuery,
    type MeasuringPointLeqAlarmExceedancesQueryVariables,
} from '@/graphql/types';
import { DATA_TYPE } from '../../../../../../graph/enums';
import { locale$, secondsToDurationText } from '../../../../../../utils/date-fns';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import {
    buildSoundAlarmInput,
    buildSoundAlarmInputWithLimit,
    createSoundAlarmExceedanceResultLinks,
    formatSoundAlarmLimitReachedNote,
} from '../soundAlarmExceedances';
import { createTimeRangeColumn } from '../tabular';
import { toSoundMeasuringPointRelayId } from '../measuringPointInput';
import { RuntimeDataService } from '../types';
import type { RuntimeDataServiceRequest, RuntimeTabularColumnDescriptor } from '../types';
import {
    isRecord,
    normalizeArrayEntries,
    normalizeNullableInteger,
    normalizeOptionalPositiveInteger,
    normalizeOrderedTimeWindow,
    normalizeRequiredFiniteNumber,
    normalizeRequiredId,
    normalizeRequiredInteger,
    normalizeString,
} from '../validationPrimitives';

type LeqAlarmExceedanceEntry =
    RuntimeDataServiceOutput<'measuring_point.leq_alarm_exceedances'>['entries'][number];
type LeqAlarmSummaryEntry =
    RuntimeDataServiceOutput<'measuring_point.leq_alarm_exceedances'>['alarms'][number];

function formatAlarmInterval(seconds: number): string {
    return secondsToDurationText(seconds, locale$.getValue());
}

function normalizeLeqAlarmExceedanceEntry(
    value: unknown,
    pathLabel: string
): LeqAlarmExceedanceEntry {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        startTime: normalizeRequiredFiniteNumber(value.startTime, `${pathLabel}.startTime`),
        endTime: normalizeRequiredFiniteNumber(value.endTime, `${pathLabel}.endTime`),
        name: normalizeString(value.name, `${pathLabel}.name`),
        alarmInterval: normalizeRequiredInteger(value.alarmInterval, `${pathLabel}.alarmInterval`),
        alarmLevel: normalizeRequiredFiniteNumber(value.alarmLevel, `${pathLabel}.alarmLevel`),
        value: normalizeRequiredFiniteNumber(value.value, `${pathLabel}.value`),
    };
}

function normalizeLeqAlarmSummaryEntry(value: unknown, pathLabel: string): LeqAlarmSummaryEntry {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        name: normalizeString(value.name, `${pathLabel}.name`),
        alarmInterval: normalizeRequiredInteger(value.alarmInterval, `${pathLabel}.alarmInterval`),
        alarmLevel: normalizeRequiredFiniteNumber(value.alarmLevel, `${pathLabel}.alarmLevel`),
        counts: normalizeRequiredInteger(value.counts, `${pathLabel}.counts`),
    };
}

function validateLeqAlarmExceedancesInput(
    input: unknown
): RuntimeDataServiceInput<'measuring_point.leq_alarm_exceedances'> {
    if (!isRecord(input)) {
        throw new Error('Expected "measuring_point.leq_alarm_exceedances" input to be an object.');
    }

    const { startTime, endTime } = normalizeOrderedTimeWindow(
        input.startTime,
        input.endTime,
        'measuring_point.leq_alarm_exceedances'
    );

    const validatedLimit = normalizeOptionalPositiveInteger(
        input.limit,
        'measuring_point.leq_alarm_exceedances.limit'
    );

    return {
        measuringPointId: normalizeRequiredId(
            input.measuringPointId,
            'measuring_point.leq_alarm_exceedances.measuringPointId'
        ),
        startTime,
        endTime,
        ...(typeof validatedLimit === 'undefined' ? {} : { limit: validatedLimit }),
    };
}

function validateLeqAlarmExceedancesResult(
    result: unknown
): RuntimeDataServiceOutput<'measuring_point.leq_alarm_exceedances'> {
    if (!isRecord(result)) {
        throw new Error('Expected "measuring_point.leq_alarm_exceedances" result to be an object.');
    }

    const limitReachedValue = normalizeNullableInteger(
        result.limitReachedValue,
        'measuring_point.leq_alarm_exceedances.limitReachedValue'
    );

    return {
        entries: normalizeArrayEntries(
            result.entries,
            'measuring_point.leq_alarm_exceedances.entries',
            normalizeLeqAlarmExceedanceEntry
        ),
        alarms: normalizeArrayEntries(
            result.alarms,
            'measuring_point.leq_alarm_exceedances.alarms',
            normalizeLeqAlarmSummaryEntry
        ),
        limitReachedValue,
        limitReachedNote: formatSoundAlarmLimitReachedNote(limitReachedValue),
    };
}

function parseEntriesLinkValue(value: unknown): readonly LeqAlarmExceedanceEntry[] {
    return normalizeArrayEntries(
        value,
        'measuring_point.leq_alarm_exceedances.entries',
        normalizeLeqAlarmExceedanceEntry
    );
}

function parseAlarmsLinkValue(value: unknown): readonly LeqAlarmSummaryEntry[] {
    return normalizeArrayEntries(
        value,
        'measuring_point.leq_alarm_exceedances.alarms',
        normalizeLeqAlarmSummaryEntry
    );
}

function createEntriesColumns() {
    return [
        createTimeRangeColumn<LeqAlarmExceedanceEntry>(),
        {
            kind: 'text',
            key: 'name',
            label: gettext('ALARM_NAME'),
            defaultAlignment: 'left',
            resolveCell: (entry: LeqAlarmExceedanceEntry) => entry.name,
        },
        {
            kind: 'text',
            key: 'alarm_interval',
            label: gettext('LEQ_ALARM_INTERVAL'),
            defaultAlignment: 'left',
            resolveCell: (entry: LeqAlarmExceedanceEntry) =>
                formatAlarmInterval(entry.alarmInterval),
        },
        {
            kind: 'measurement',
            key: 'alarm_level',
            label: gettext('ALARM_LEVEL'),
            dataType: DATA_TYPE.LXEQ,
            defaultAlignment: 'right',
            resolveCell: (entry: LeqAlarmExceedanceEntry) => entry.alarmLevel,
        },
        {
            kind: 'measurement',
            key: 'value',
            label: gettext('LEQ_ALARM_MEASURED'),
            dataType: DATA_TYPE.LXEQ,
            defaultAlignment: 'right',
            resolveCell: (entry: LeqAlarmExceedanceEntry) => entry.value,
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<LeqAlarmExceedanceEntry>[];
}

function createAlarmSummaryColumns() {
    return [
        {
            kind: 'text',
            key: 'name',
            label: gettext('ALARM_NAME'),
            defaultAlignment: 'left',
            resolveCell: (entry: LeqAlarmSummaryEntry) => entry.name,
        },
        {
            kind: 'text',
            key: 'alarm_interval',
            label: gettext('LEQ_ALARM_INTERVAL'),
            defaultAlignment: 'left',
            resolveCell: (entry: LeqAlarmSummaryEntry) => formatAlarmInterval(entry.alarmInterval),
        },
        {
            kind: 'measurement',
            key: 'alarm_level',
            label: gettext('ALARM_LEVEL'),
            dataType: DATA_TYPE.LXEQ,
            defaultAlignment: 'right',
            resolveCell: (entry: LeqAlarmSummaryEntry) => entry.alarmLevel,
        },
        {
            kind: 'count',
            key: 'counts',
            label: gettext('EXCEEDANCE_COUNT'),
            defaultAlignment: 'right',
            resolveCell: (entry: LeqAlarmSummaryEntry) => entry.counts,
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<LeqAlarmSummaryEntry>[];
}

export class LeqAlarmExceedancesRuntimeDataService extends RuntimeDataService<'measuring_point.leq_alarm_exceedances'> {
    static readonly serviceId = 'measuring_point.leq_alarm_exceedances' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_LEQ_ALARM_EXCEEDANCES_LABEL'),
            description: gettext('DATA_SERVICE_LEQ_ALARM_EXCEEDANCES_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        const entriesColumns = createEntriesColumns();
        const alarmSummaryColumns = createAlarmSummaryColumns();

        return createSoundAlarmExceedanceResultLinks({
            entries: {
                label: gettext('DATA_SERVICE_LEQ_ALARM_EXCEEDANCES_ENTRIES_LABEL'),
                description: gettext('DATA_SERVICE_LEQ_ALARM_EXCEEDANCES_ENTRIES_DESCRIPTION'),
                columns: entriesColumns,
                parseTableRows: parseEntriesLinkValue,
                buildInput: (args) =>
                    buildSoundAlarmInputWithLimit(args, {
                        serviceLabel: 'LEQ alarm exceedances',
                        argPath: 'measuring_point.leq_alarm_exceedances.entries.arg',
                    }),
            },
            alarms: {
                label: gettext('DATA_SERVICE_LEQ_ALARM_EXCEEDANCES_ALARMS_LABEL'),
                description: gettext('DATA_SERVICE_LEQ_ALARM_EXCEEDANCES_ALARMS_DESCRIPTION'),
                columns: alarmSummaryColumns,
                parseTableRows: parseAlarmsLinkValue,
                buildInput: (args) => buildSoundAlarmInput(args, 'LEQ alarm exceedances'),
            },
            limitReachedNote: {
                label: gettext('DATA_SERVICE_LEQ_ALARM_EXCEEDANCES_LIMIT_REACHED_NOTE_LABEL'),
                description: gettext(
                    'DATA_SERVICE_LEQ_ALARM_EXCEEDANCES_LIMIT_REACHED_NOTE_DESCRIPTION'
                ),
                buildInput: (args) =>
                    buildSoundAlarmInputWithLimit(args, {
                        serviceLabel: 'LEQ alarm exceedances',
                        argPath: 'measuring_point.leq_alarm_exceedances.limit_reached_note.arg',
                    }),
            },
        });
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateLeqAlarmExceedancesInput;

    validateResult = validateLeqAlarmExceedancesResult;

    createResult$ = ({
        input,
    }: RuntimeDataServiceRequest<'measuring_point.leq_alarm_exceedances'>) =>
        from(
            this.client.query<
                MeasuringPointLeqAlarmExceedancesQuery,
                MeasuringPointLeqAlarmExceedancesQueryVariables
            >({
                query: MeasuringPointLeqAlarmExceedancesDocument,
                variables: {
                    measuringPointId: toSoundMeasuringPointRelayId(input.measuringPointId),
                    startTime: input.startTime,
                    endTime: input.endTime,
                    ...(typeof input.limit === 'undefined' ? {} : { limit: input.limit }),
                },
                fetchPolicy: 'cache-first',
            })
        ).pipe(map(({ data }) => validateLeqAlarmExceedancesResult(data.leqAlarmExceedances)));
}

export function createLeqAlarmExceedancesService(
    client: ApolloClient<object>
): LeqAlarmExceedancesRuntimeDataService {
    return new LeqAlarmExceedancesRuntimeDataService(client);
}
